import Utils from "context/utils";

export default class SessionManager {
  constructor(app, revision, updateProgress, onUpdate) {
    this.app = app;
    this.revision = revision;
    this.updateProgress = updateProgress;
    this.listeners = new Set();
    if (onUpdate) {
      this.listeners.add(onUpdate);
    }

    // Session state
    this.sessions = null;
    this.sessionId = null;
    this.session = null;
    this.sessionObjects = null;
    this.sessionData = {};
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    this.loadingSessionObjects = new Set();
    this.threadEventListeners = new Map();   // alias -> Set<callback>
    this.graphEventListeners = new Map();    // alias -> Set<callback>
    
    // Sync state
    this.sessionSync = false;
    this.sessionSyncCounter = 0;
    this.lastSessionId = null;

    // Subscriptions
    this.subscriptions = {};
    
    // Timer for auto-refresh
    this.mounted = true;
    this.autoRefreshTimer = null;
  }

  destroy() {
    this.mounted = false;
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
    }
  }

  /**
   * Subscriptions
   */

  subscribe(alias, callback) {
    if (!this.subscriptions[alias]) {
      this.subscriptions[alias] = [];
    }
    this.subscriptions[alias].push(callback);
    
    // If data is already available, notify immediately
    if (this.sessionData[alias]) {
      callback(this.sessionData[alias]);
    }

    return () => {
      this.unsubscribe(alias, callback);
    }
  }

  unsubscribe(alias, callback) {
    if (!this.subscriptions[alias]) {
      return;
    }
    this.subscriptions[alias] = this.subscriptions[alias].filter(cb => cb !== callback);
  }

  notify(alias, value) {
    if (!this.subscriptions[alias]) {
      return;
    }
    for (const callback of this.subscriptions[alias]) {
      try {
        callback(value);
      } catch (e) {
        console.error(`Error notifying subscription for ${alias}:`, e);
      }
    }
  }

  addListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  notifyListeners() {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (e) {
        console.error("Error notifying listener:", e);
      }
    }
  }

  /**
   * Thread event listeners (alias-scoped, fired on session_thread_item_posted events)
   */

  addThreadEventListener(alias, callback) {
    if (!this.threadEventListeners.has(alias)) {
      this.threadEventListeners.set(alias, new Set());
    }
    this.threadEventListeners.get(alias).add(callback);
  }

  removeThreadEventListener(alias, callback) {
    const set = this.threadEventListeners.get(alias);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) {
      this.threadEventListeners.delete(alias);
    }
  }

  notifyThreadEventListeners(alias, payload) {
    const set = this.threadEventListeners.get(alias);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error(`Error notifying thread listener for ${alias}:`, e);
      }
    }
  }

  /**
   * Graph event listeners (alias-scoped, fired on session_graph_changed events)
   */

  addGraphEventListener(alias, callback) {
    if (!this.graphEventListeners.has(alias)) {
      this.graphEventListeners.set(alias, new Set());
    }
    this.graphEventListeners.get(alias).add(callback);
  }

  removeGraphEventListener(alias, callback) {
    const set = this.graphEventListeners.get(alias);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) {
      this.graphEventListeners.delete(alias);
    }
  }

  notifyGraphEventListeners(alias, payload) {
    const set = this.graphEventListeners.get(alias);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error(`Error notifying graph listener for ${alias}:`, e);
      }
    }
  }

  /**
   * Data Access
   */

  getSessionData(alias) {
    return this.sessionData[alias];
  }

  /**
   * Actions
   */

  clearSessionState(session) {
    // Treat an empty/missing session as "no session selected" so the details
    // pane doesn't get stuck rendering Session Objects with sessionObjects=null
    // (which surfaces as a permanent "Loading session objects..." alert).
    const hasSession = session && session["id"];
    this.sessionId = hasSession ? session["id"] : null;
    this.session = hasSession ? session : null;
    this.sessionObjects = null;
    this.sessionData = {};
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    this.loadingSessionObjects.clear();
    // Drop stale thread/graph listeners; subscribers re-subscribe in componentDidUpdate.
    this.threadEventListeners = new Map();
    this.graphEventListeners = new Map();
  }

  loadSessions(more) {
    this.updateProgress(true, more ? "Loading more sessions..." : "Loading sessions...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "list_sessions"
    };

    if (more) {
      body["start_session_id"] = this.lastSessionId;
    }

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        let sessions = response.data.sessions;
        this.lastSessionId = response.data.last_session_id;

        if (more) {
          this.sessions = (this.sessions || []).concat(sessions);
        } else {
            this.sessions = sessions;
        }
        
        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();

        return this.sessions;
      }, error => {
        this.updateProgress(false, null, true, "Failed to load sessions: " + error.message);
      });
  }

  loadSessionContent() {
    const body = {
      "engine": "workflow_blobhub",
      "command": "list_session_objects",
      "session_id": this.sessionId
    };
    
    const requestedSessionId = this.sessionId;

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        this.sessionObjects = (response.data["objects"] || []).map(obj => {
          if (obj.updated_at) {
            obj.updatedAt = obj.updated_at;
            delete obj.updated_at;
          }
          return obj;
        });
        
        for (const object of this.sessionObjects) {
          this.loadSessionObject(object["alias"]);
        }
        this.notifyListeners();
      }, error => {
        console.error("Failed to load session content: " + error.message);
      });
  }

  loadSessionObject(alias) {
    const body = {
      "engine": "workflow_blobhub",
      "command": "download_session_object",
      "session_id": this.sessionId,
      "alias": alias
    };
    
    this.loadingSessionObjects.add(alias);
    this.notifyListeners();

    const requestedSessionId = this.sessionId;

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        const object = response.data["object"];
        const value = object["value"];
        this.sessionData[alias] = value;
        
        // Update session object metadata
        if (this.sessionObjects) {
          const sessionObject = this.sessionObjects.find(obj => obj.alias === alias);
          if (sessionObject) {
            sessionObject.updatedAt = object.updated_at;
          } else {
            this.sessionObjects.push({
              alias: alias,
              updatedAt: object.updated_at
            });
            this.sessionObjects.sort((a, b) => a.alias.localeCompare(b.alias));
          }
        }

        this.loadingSessionObjects.delete(alias);
        this.notify(alias, value);
        this.notifyListeners();
      }, error => {
        console.error(`Failed to load session object ${alias}: ` + error.message);
        this.loadingSessionObjects.delete(alias);
        this.notifyListeners();
      });
  }

  autoRefreshSessionEvents() {
    if (!this.mounted) return;

    this.autoRefreshTimer = setTimeout(() => {
      if (!this.mounted) return;

      if (this.sessionSync) {
          if (this.sessionSyncCounter > 180) {
              this.enableSessionSync(false);
          } else {
              this.sessionSyncCounter++;
          }
      }

      if (this.sessionSync && this.session && this.session["status"] === "open") {
          this.loadRecentSessionEvents();
      }

      this.autoRefreshSessionEvents();
    }, 1000);
  }

  enableSessionSync(enable) {
    this.sessionSync = enable;
    this.sessionSyncCounter = 0;

    if (enable && this.sessionEventCreatedAt === null) {
      // First activation for this session — anchor the cursor at "now"
      // so we don't replay the entire session history on the next poll.
      // `sessionEventCreatedAt` is nulled in `clearSessionState` on
      // session change, so each new session re-anchors here.
      //
      // Subsequent OFF -> ON toggles intentionally DO NOT reset the
      // cursor: the next polling cycle picks up every session event
      // posted while sync was paused — session_object_modified,
      // session_object_deleted, AND session_thread_item_posted — and
      // dispatches them to listeners. That's how a thread widget
      // re-tails items posted by other actors during the sync gap.
      this.sessionEventCreatedAt = new Date().toISOString();
    }

    this.notifyListeners();
  }

  loadRecentSessionEvents() {
    let createdSince = this.sessionEventCreatedAt;
    this.loadSessionEvents(createdSince, true);
  }

  loadSessionEvents(createdSince, ascending) {
    const body = {
      "engine": "workflow_blobhub",
      "command": "list_session_events",
      "session_id": this.sessionId,
      "include_session": true,
      "ascending": ascending
    };

    if (createdSince) {
      body["created_since"] = createdSince;
    }

    const requestedSessionId = this.sessionId;

    this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        const session = response.data["session"];
        const sessionEvents = response.data["session_events"];

        this.session = session;
        this.sessionEvents = sessionEvents;

        if (sessionEvents.length > 0) {
            this.sessionEventCreatedAt = sessionEvents[sessionEvents.length - 1]["created_at"];
        }

        this.refreshSessionObjects(sessionEvents);
      }, error => {
        console.error("Failed to load session events: " + error.message);
      });
  }

  refreshSessionObjects(sessionEvents) {
    const aliasesToRefresh = new Set();
    let hasDeletions = false;

    for (const sessionEvent of sessionEvents) {
      if ("session_object_modified" === sessionEvent["type"]) {
        aliasesToRefresh.add(sessionEvent["session_object"]["alias"]);
      } else if ("session_object_deleted" === sessionEvent["type"]) {
        const alias = sessionEvent["session_object"]["alias"];
        
        if (this.sessionObjects) {
          const initialLength = this.sessionObjects.length;
          this.sessionObjects = this.sessionObjects.filter(obj => obj.alias !== alias);
          if (this.sessionObjects.length !== initialLength) hasDeletions = true;
        }
        
        if (alias in this.sessionData) {
            delete this.sessionData[alias];
            hasDeletions = true;
        }
        
        // If deleted, don't refresh even if it was modified in the same batch (unlikely but safe)
        aliasesToRefresh.delete(alias);
      }
    }

    for (const alias of aliasesToRefresh) {
      this.loadSessionObject(alias);
    }

    if (hasDeletions) {
      this.notifyListeners();
    }

    // Dispatch thread events. The backend emits payload {session_object: {alias, item_id}}
    // for all three session-object event types (modified, deleted, thread_item_posted);
    // ExecutionEventsImpl.post_session_event merges that into the event row, so the
    // alias/item_id live under sessionEvent.session_object. We continue to pass a flat
    // {alias, item_id} shape to widget listeners so widgets/thread/index.js doesn't change.
    for (const sessionEvent of sessionEvents) {
      if ("session_thread_item_posted" === sessionEvent["type"]) {
        const so = sessionEvent["session_object"] || {};
        if (so.alias) {
          this.notifyThreadEventListeners(so.alias, { alias: so.alias, item_id: so.item_id });
        }
      } else if ("session_graph_changed" === sessionEvent["type"]) {
        const so = sessionEvent["session_object"] || {};
        if (so.alias) {
          this.notifyGraphEventListeners(so.alias, { alias: so.alias, changes: so.changes });
        }
      }
    }
  }

  createNewSession(session) {
    this.updateProgress(true, "Creating a new session...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "create_session"
    };

    if (session) {
      if (session.description && session.description.trim()) {
        body["description"] = session.description.trim();
      }
      if (session.tracing) {
        body["tracing"] = session.tracing;
      }
    }

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        const newSession = response.data["session"];
        this.sessions = [newSession].concat(this.sessions || []);
        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();
        return newSession;
      }, error => {
        this.updateProgress(false, null, true, "Failed to create new session: " + error.message);
      });
  }

  uploadSessionObject(alias, value) {
    this.updateProgress(true, "Updating session object...", false, null);
    
    const body = {
      engine: "workflow_blobhub",
      command: "upload_session_object",
      session_id: this.sessionId,
      alias: alias,
      value: value
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        this.sessionData[alias] = value;
        
        // Update session objects list
        if (this.sessionObjects) {
          const existingIndex = this.sessionObjects.findIndex(obj => obj.alias === alias);
          if (existingIndex !== -1) {
            // Update existing
            this.sessionObjects[existingIndex].updatedAt = new Date().toISOString();
          } else {
            // Add new
            this.sessionObjects.push({
              alias: alias,
              updatedAt: new Date().toISOString()
            });
            // Sort by alias
            this.sessionObjects.sort((a, b) => a.alias.localeCompare(b.alias));
          }
        }

        this.notify(alias, value);
        this.notifyListeners();
      }, error => {
        this.updateProgress(false, null, true, "Failed to update session object: " + error.message);
      });
  }

  deleteSessionObject(alias) {
    this.updateProgress(true, "Deleting session object...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "delete_session_object",
      session_id: this.sessionId,
      alias: alias
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        
        // Remove from session objects list
        if (this.sessionObjects) {
          this.sessionObjects = this.sessionObjects.filter(obj => obj.alias !== alias);
        }
        
        // Remove data
        delete this.sessionData[alias];
        
        // Notify listeners
        this.notifyListeners();
      }, error => {
        this.updateProgress(false, null, true, "Failed to delete session object: " + error.message);
      });
  }
}
